import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import ServiceCard from './ServiceCard';
import { useTheme } from '../context/ThemeContext';

const services = [
  // Core
  { title: 'Website Development', desc: 'Custom Next.js/React sites with SEO, speed, and analytics baked in.', icon: '🌐', category: 'Web', tags: ['Next.js', 'SEO', 'SSR'] },
  { title: 'Application Development', desc: 'Native & cross-platform apps with React Native/Flutter and robust APIs.', icon: '📱', category: 'App', tags: ['React Native', 'Flutter'] },
  { title: 'Software Development', desc: 'Tailored software, integrations, and microservices for scale.', icon: '💻', category: 'Software', tags: ['Node.js', 'Python'] },
  { title: 'UI/UX Design', desc: 'Figma-driven design systems and delightful, conversion-focused UX.', icon: '🎨', category: 'Design', tags: ['Figma', 'Design System'] },
  { title: 'Power BI & Excel Dashboard', desc: 'Realtime KPI dashboards + automated reporting pipelines.', icon: '📊', category: 'Data', tags: ['Power BI', 'ETL', 'Excel'] },
  { title: 'Digital Marketing Services', desc: 'Performance marketing, SEO/ASO, content engines that compound.', icon: '📣', category: 'Marketing', tags: ['SEO', 'ASO', 'Content'] },
  // Advanced
  { title: 'AI & Agentic Automation', desc: 'Autonomous agents reconcile orders, update sheets & trigger workflows.', icon: '🤖', category: 'AI', tags: ['Agents', 'Workflows', 'RPA'] },
  { title: 'AI Chatbots & WhatsApp Bots', desc: 'Smart support & lead-gen bots connected to catalog and CRM.', icon: '💬', category: 'AI', tags: ['WhatsApp', 'OpenAI', 'CRM'] },
  { title: 'Affiliate & Influencer Ops', desc: 'Trackable links, payouts, creator onboarding — built to scale.', icon: '🧩', category: 'Marketing', tags: ['Affiliate', 'Creators', 'Attribution'] },
  { title: 'Personalization Engine', desc: 'On-site recommendations and journeys tailored to each visitor.', icon: '✨', category: 'Growth', tags: ['Recs', 'Segmentation', 'CDP'] },
  { title: 'Brand & Founder Persona', desc: 'Positioning, narrative, and content system for the founder brand.', icon: '🧭', category: 'Brand', tags: ['LinkedIn', 'YouTube', 'Narrative'] },
];

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

export default function ServicesSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // THEME TOKENS (light/dark)
  const TOKENS = isDark
    ? {
        bgStart: '#0B1220',
        bgMid: '#0B1325',
        bgEnd: '#000000',
        orbBrand: 'rgba(31,165,80,0.22)',
        orbAccent: 'rgba(6,182,212,0.18)',
        grid: 'rgba(255,255,255,0.25)',
        card: '#0E1422',
        cardBorder: 'rgba(255,255,255,0.10)',
        text: '#FFFFFF',
        muted: 'rgba(255,255,255,0.70)',
        kpiBg: 'rgba(255,255,255,0.05)',
        kpiRing: 'rgba(255,255,255,0.10)',
        ribbonFrom: 'rgba(31,165,80,0.08)',
        ribbonTo: 'rgba(6,182,212,0.08)',
        shadow: '0 12px 24px rgba(0,0,0,0.25)',
      }
    : {
        bgStart: '#F6FAFF',
        bgMid: '#F0F4F9',
        bgEnd: '#FFFFFF',
        orbBrand: 'rgba(31,165,80,0.14)',
        orbAccent: 'rgba(6,182,212,0.12)',
        grid: 'rgba(0,0,0,0.08)',
        card: '#FFFFFF',
        cardBorder: 'rgba(0,0,0,0.08)',
        text: '#0B1220',
        muted: 'rgba(17,24,39,0.64)',
        kpiBg: 'rgba(15,23,42,0.03)',
        kpiRing: 'rgba(0,0,0,0.06)',
        ribbonFrom: 'rgba(31,165,80,0.10)',
        ribbonTo: 'rgba(6,182,212,0.10)',
        shadow: '0 16px 28px rgba(2,6,23,0.10)',
      };

  const [filter, setFilter] = useState('All');
  const [query, setQuery] = useState('');

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(services.map((s) => s.category)))],
    []
  );

  const filteredServices = useMemo(() => {
    const base = filter === 'All' ? services : services.filter((s) => s.category === filter);
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.desc.toLowerCase().includes(q) ||
        (s.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [filter, query]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    name: 'MonkeyTech Services',
    itemListElement: services.map((s) => ({
      '@type': 'Offer',
      name: s.title,
      description: s.desc,
      category: s.category,
    })),
  };

  return (
    <section
      className="relative min-h-screen py-20 px-4 sm:px-6 lg:px-8 font-inter overflow-hidden text-[var(--text)]"
      aria-labelledby="services-heading"
      style={{
        '--text': TOKENS.text,
        '--muted': TOKENS.muted,
        '--card': TOKENS.card,
        '--cardBorder': TOKENS.cardBorder,
        '--grid': TOKENS.grid,
        '--orbBrand': TOKENS.orbBrand,
        '--orbAccent': TOKENS.orbAccent,
        '--ribbonFrom': TOKENS.ribbonFrom,
        '--ribbonTo': TOKENS.ribbonTo,
      }}
    >
      {/* ===== Background: responsive gradient + grid + brand orbs ===== */}
      <div className="absolute inset-0 -z-10">
        {/* Gradient base */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${TOKENS.bgStart}, ${TOKENS.bgMid}, ${TOKENS.bgEnd})`,
          }}
        />
        {/* Brand orbs */}
        <div
          className="absolute -top-24 -left-24 h-[36rem] w-[36rem] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(closest-side, var(--orbBrand), transparent)' }}
        />
        <div
          className="absolute bottom-0 right-[-8rem] h-[28rem] w-[28rem] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(closest-side, var(--orbAccent), transparent)' }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.06] [mask-image:linear-gradient(to_bottom,black,transparent_95%)]"
          style={{
            backgroundImage:
              'linear-gradient(var(--grid) 1px, transparent 1px), linear-gradient(90deg, var(--grid) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* Fine noise for light mode depth */}
        {!isDark && (
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.04]"
            style={{
              backgroundImage:
                'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27 viewBox=%270 0 120 120%27><filter id=%27n%27 x=%270%27 y=%270%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.8%27 numOctaves=%272%27 stitchTiles=%27stitch%27/></filter><rect width=%27120%27 height=%27120%27 filter=%27url(%23n)%27 opacity=%270.25%27/></svg>")',
            }}
          />
        )}
      </div>

      {/* ===== Marquee (feature highlights) ===== */}
      <div className="relative z-10 mx-auto max-w-7xl">
        <div
          className="overflow-hidden rounded-xl ring-1 backdrop-blur"
          style={{ background: `var(--card)`, boxShadow: TOKENS.shadow, borderColor: 'var(--cardBorder)' }}
        >
          <div className="flex gap-8 whitespace-nowrap py-2 animate-[marquee_24s_linear_infinite]">
            {['Next.js storefronts', 'AI chatbots on WhatsApp', 'Agentic automations', 'Power BI dashboards', 'SEO + performance', 'Creator/Affiliate ops'].map((x, i) => (
              <span key={i} className="text-xs sm:text-sm px-4" style={{ color: 'var(--muted)' }}>{x}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ===== Heading ===== */}
      <motion.div
        className="text-center mt-10 sm:mt-12 mb-10 sm:mb-12 relative z-10 max-w-4xl mx-auto"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <h2 id="services-heading" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
          Powering Modern{' '}
          <span className="bg-gradient-to-r from-[var(--brand,#1FA550)] to-[var(--accent,#06B6D4)] text-transparent bg-clip-text">
            Tech Businesses
          </span>
        </h2>
        <p className="mt-5 text-lg sm:text-xl" style={{ color: 'var(--muted)' }}>
          Choose from full-stack services and plug-and-play growth accelerators.
        </p>
      </motion.div>

      {/* ===== Controls ===== */}
      <div className="relative z-10 max-w-7xl mx-auto mb-8">
        <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-6">
          {/* Search */}
          <label className="relative w-full lg:max-w-md">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search services, e.g. chatbot, SEO, dashboard…"
              className="w-full rounded-xl ring-1 focus:ring-2 px-4 py-3 outline-none backdrop-blur placeholder:text-[var(--muted)]"
              style={{
                background: 'var(--card)',
                color: 'var(--text)',
                boxShadow: TOKENS.shadow,
                borderColor: 'var(--cardBorder)',
                '--tw-ring-color': 'var(--cardBorder)',
              }}
              aria-label="Search services"
            />
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }}>
              ⌘K
            </span>
          </label>

          {/* Filters */}
          <nav className="flex flex-wrap justify-center gap-2 lg:ml-auto" aria-label="Service category filter">
            {categories.map((category) => {
              const active = filter === category;
              return (
                <motion.button
                  key={category}
                  onClick={() => setFilter(category)}
                  className="px-4 py-2 rounded-full text-sm font-semibold ring-1 transition-all duration-300"
                  style={{
                    background: active ? 'var(--brand, #1FA550)' : 'var(--card)',
                    color: active ? '#000' : 'var(--muted)',
                    borderColor: 'var(--cardBorder)',
                    boxShadow: TOKENS.shadow,
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.96 }}
                  aria-current={active ? 'true' : 'false'}
                >
                  {category}
                </motion.button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* ===== Cards ===== */}
      <motion.div
        className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
      >
        {filteredServices.map((service, index) => (
          <ServiceCard
            key={service.title}
            title={service.title}
            desc={service.desc}
            icon={service.icon}
            tags={service.tags}
            index={index}
            isDark={isDark}     // pass theme to card
          />
        ))}
      </motion.div>

      {/* ===== KPI Strip ===== */}
      <motion.ul
        className="relative z-10 max-w-7xl mx-auto mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        aria-label="MonkeyTech performance stats"
      >
        {[
          { k: '+120', v: 'Projects Delivered' },
          { k: '4.9/5', v: 'Client CSAT' },
          { k: '99.9%', v: 'Infra Uptime' },
          { k: '+300', v: 'Automations Built' },
        ].map((s) => (
          <li
            key={s.v}
            className="rounded-2xl p-4 text-center ring-1 backdrop-blur"
            style={{ background: TOKENS.kpiBg, borderColor: TOKENS.kpiRing, boxShadow: TOKENS.shadow }}
          >
            <div className="text-2xl font-extrabold">{s.k}</div>
            <div style={{ color: 'var(--muted)' }} className="text-sm">{s.v}</div>
          </li>
        ))}
      </motion.ul>

      {/* ===== Trust Bar ===== */}
      <div className="relative z-10 max-w-7xl mx-auto mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 opacity-90">
        {['Kalamatwala', 'Madhusudan Tex Fab', 'D2C', 'Retail+'].map((n) => (
          <span key={n} className="text-sm sm:text-base" style={{ color: 'var(--muted)' }}>
            {n}
          </span>
        ))}
      </div>

      {/* ===== Ribbon CTA ===== */}
      <motion.div
        className="relative z-10 max-w-7xl mx-auto mt-12"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        viewport={{ once: true }}
      >
        <div
          className="rounded-2xl p-5 sm:p-6 ring-1 backdrop-blur"
          style={{
            background: 'linear-gradient(90deg, var(--ribbonFrom), var(--ribbonTo))',
            borderColor: 'var(--cardBorder)',
            boxShadow: TOKENS.shadow,
          }}
        >
          <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div>
              <p className="text-lg font-semibold">Not sure where to start?</p>
              <p style={{ color: 'var(--muted)' }}>Tell us your goal — we’ll assemble a roadmap in 24 hours.</p>
            </div>
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-5 py-3 rounded-xl text-black font-semibold hover:opacity-95"
              style={{ background: 'var(--brand, #1FA550)' }}
            >
              Get a Free Plan
              <svg className="ml-2" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14m0 0-6-6m6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </motion.div>

      {/* ===== SEO JSON-LD ===== */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Local helpers */}
      <style>{`
        @keyframes marquee { 0% { transform: translateX(0) } 100% { transform: translateX(-50%) } }
        .font-inter{font-family:Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial}
      `}</style>
    </section>
  );
}
